import { Search } from "lucide-react";
import * as React from "react";
import { useMemo, useState } from "react";

import { Button, Checkbox, Input, Label } from "../ui";

interface SelectableWorkspace {
	id: string;
	name: string;
	isDefault: boolean;
}

interface WorkspaceSelectFormProps {
	workspaces: SelectableWorkspace[];
	onSubmit: (workspaceIds: string[]) => void;
}

/**
 * Searchable checkbox grid of an organization's workspaces. The organization's default workspace starts selected.
 */
export function WorkspaceSelectForm({ workspaces, onSubmit }: WorkspaceSelectFormProps) {
	const [search, setSearch] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(
		() =>
			new Set(
				workspaces
					.filter((workspace) => workspace.isDefault)
					.map((workspace) => workspace.id),
			),
	);

	const visibleWorkspaces = useMemo(() => {
		const query = search.trim().toLowerCase();
		return query
			? workspaces.filter((workspace) => workspace.name.toLowerCase().includes(query))
			: workspaces;
	}, [workspaces, search]);

	const visibleSelectedCount = visibleWorkspaces.filter((workspace) =>
		selectedIds.has(workspace.id),
	).length;
	const allVisibleSelected =
		visibleWorkspaces.length > 0 && visibleSelectedCount === visibleWorkspaces.length;

	const setSelected = (ids: string[], selected: boolean) => {
		setSelectedIds((current) => {
			const next = new Set(current);
			ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
			return next;
		});
	};

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				onSubmit([...selectedIds]);
			}}
			className="flex flex-col gap-6"
		>
			<div className="relative">
				<Search className="absolute top-3 left-4 size-4 text-stealth-gray" />
				<Input
					className="!max-w-none pl-11 border-stealth-border dark:bg-stealth-gradient-blue dark:text-white"
					type="text"
					value={search}
					placeholder="Search workspaces"
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>

			<div className="flex flex-col">
				<Label className="flex items-center gap-4 pb-4 border-b border-stealth-border cursor-pointer">
					<Checkbox
						className="size-6 rounded"
						checked={
							allVisibleSelected
								? true
								: visibleSelectedCount > 0
									? "indeterminate"
									: false
						}
						onCheckedChange={(checked) =>
							setSelected(
								visibleWorkspaces.map((workspace) => workspace.id),
								checked === true,
							)
						}
					/>
					<span className="flex-1 text-base font-bold uppercase tracking-wide text-white">
						All workspaces
					</span>
					<span className="text-sm italic text-stealth-gray">
						{selectedIds.size} of {workspaces.length}
					</span>
				</Label>

				<div className="h-96 overflow-y-auto">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
						{visibleWorkspaces.map((workspace) => (
							<Label
								key={workspace.id}
								className="flex items-center gap-4 py-4 border-b border-stealth-border text-base text-white cursor-pointer"
							>
								<Checkbox
									className="size-6 rounded"
									checked={selectedIds.has(workspace.id)}
									onCheckedChange={(checked) =>
										setSelected([workspace.id], checked === true)
									}
								/>
								<span className="truncate">{workspace.name}</span>
							</Label>
						))}
					</div>
				</div>
			</div>

			<Button type="submit" size="lg" disabled={selectedIds.size === 0}>
				Continue with {selectedIds.size}{" "}
				{selectedIds.size === 1 ? "workspace" : "workspaces"}
			</Button>
		</form>
	);
}
